import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { useNavigate, useLocation, Outlet } from 'react-router-dom'
import { KeyRound, LayoutDashboard, LogOut, ShieldOff, Users } from 'lucide-react'
import {
  AppShell,
  Avatar,
  Icon,
  Sidebar,
  SidebarDrawer,
  Topbar,
} from '@navegasistemas/dls-navega-rjs'
import { useLogout } from '@/features/auth/hooks/use-logout'
import { useLogoutAll } from '@/features/auth/hooks/use-logout-all'
import { usePermission } from '@/features/auth/hooks/use-permission'
import { useAuth } from '@/hooks/use-auth'
import { navigationGuard } from '@/lib/navigation-guard'

const MOBILE_BP = 768
const TABLET_BP = 1024

function getScreenClass() {
  const w = typeof window !== 'undefined' ? window.innerWidth : TABLET_BP
  if (w < MOBILE_BP) return 'mobile' as const
  if (w < TABLET_BP) return 'tablet' as const
  return 'desktop' as const
}

interface NavItem {
  key: string
  label: string
  icon: ReactNode
  /** Quando definido, o item só aparece se `usePermission(permission)` for `true`. */
  permission?: string
}

const NAV_GROUPS: { label: string; items: NavItem[] }[] = [
  {
    label: 'Geral',
    items: [
      { key: '/dashboard', label: 'Dashboard', icon: <LayoutDashboard /> },
      { key: '/users', label: 'Usuários', icon: <Users />, permission: 'users:read' },
      { key: '/roles', label: 'Papéis', icon: <Icon name="shield" />, permission: 'roles:read' },
      {
        key: '/mobile-apps',
        label: 'Apps Mobile',
        icon: <Icon name="phone" />,
        permission: 'mobile_apps:read',
      },
    ],
  },
  {
    label: 'Deploy Hub',
    items: [
      { key: '/deploy-hub', label: 'Dashboard', icon: <Icon name="activity" /> },
      { key: '/deploy-hub/clients', label: 'Clientes', icon: <Icon name="building" /> },
      { key: '/deploy-hub/applications', label: 'Aplicações', icon: <Icon name="grid" /> },
      { key: '/deploy-hub/deployments', label: 'Deployments', icon: <Icon name="upload" /> },
      { key: '/deploy-hub/env-sync', label: 'Sincronização', icon: <Icon name="refresh" /> },
      { key: '/deploy-hub/logs', label: 'Logs', icon: <Icon name="file-text" /> },
      {
        key: '/deploy-hub/licenses',
        label: 'Licenças',
        icon: <Icon name="shield-check" />,
        permission: 'licenses:read',
      },
    ],
  },
]

const BREADCRUMB_LABELS: Record<string, string> = {
  '/dashboard': 'Dashboard',
  '/mobile-apps': 'Apps Mobile',
  '/users': 'Usuários',
  '/users/new': 'Novo usuário',
  '/roles': 'Papéis',
  '/change-password': 'Alterar senha',
  '/deploy-hub': 'Deploy Hub',
  '/deploy-hub/clients': 'Clientes',
  '/deploy-hub/applications': 'Aplicações',
  '/deploy-hub/deployments': 'Deployments',
  '/deploy-hub/env-sync': 'Sincronização',
  '/deploy-hub/logs': 'Logs',
  '/deploy-hub/licenses': 'Licenças',
}

function getBreadcrumbItems(pathname: string): { label: string }[] {
  if (/^\/users\/.+\/edit$/.test(pathname)) {
    return [{ label: 'Usuários' }, { label: 'Editar usuário' }]
  }
  if (/^\/mobile-apps\/.+$/.test(pathname)) {
    return [{ label: 'Apps Mobile' }, { label: 'Detalhes do app' }]
  }
  if (/^\/deploy-hub\/clients\/.+$/.test(pathname)) {
    return [{ label: 'Clientes' }, { label: 'Detalhes do cliente' }]
  }
  if (/^\/deploy-hub\/applications\/[^/]+\/releases$/.test(pathname)) {
    return [{ label: 'Aplicações' }, { label: 'Releases' }]
  }
  if (/^\/deploy-hub\/applications\/[^/]+$/.test(pathname)) {
    return [{ label: 'Aplicações' }, { label: 'Detalhes da aplicação' }]
  }
  if (/^\/deploy-hub\/deployments\/.+$/.test(pathname)) {
    return [{ label: 'Deployments' }, { label: 'Detalhes do deployment' }]
  }
  if (/^\/deploy-hub\/env-sync\/.+$/.test(pathname)) {
    return [{ label: 'Sincronização' }, { label: 'Detalhes da sincronização' }]
  }
  return [{ label: BREADCRUMB_LABELS[pathname] ?? 'Início' }]
}

function getActiveKey(pathname: string): string {
  if (pathname.startsWith('/mobile-apps')) return '/mobile-apps'
  if (pathname.startsWith('/users')) return '/users'
  if (pathname.startsWith('/roles')) return '/roles'
  if (pathname.startsWith('/deploy-hub/clients')) return '/deploy-hub/clients'
  if (pathname.startsWith('/deploy-hub/applications')) return '/deploy-hub/applications'
  if (pathname.startsWith('/deploy-hub/deployments')) return '/deploy-hub/deployments'
  if (pathname.startsWith('/deploy-hub/env-sync')) return '/deploy-hub/env-sync'
  if (pathname.startsWith('/deploy-hub/logs')) return '/deploy-hub/logs'
  if (pathname.startsWith('/deploy-hub/licenses')) return '/deploy-hub/licenses'
  if (pathname.startsWith('/deploy-hub')) return '/deploy-hub'
  return pathname
}

export function AppLayout() {
  const [screen, setScreen] = useState(getScreenClass)
  const [collapsed, setCollapsed] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)

  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { user } = useAuth()
  const { logout } = useLogout()
  const { logoutAll } = useLogoutAll()
  const canReadUsers = usePermission('users:read')
  const canReadRoles = usePermission('roles:read')
  const canReadMobileApps = usePermission('mobile_apps:read')
  const canReadLicenses = usePermission('licenses:read')

  useEffect(() => {
    function handleResize() {
      const next = getScreenClass()
      setScreen(next)
      if (next !== 'mobile') setDrawerOpen(false)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  function isNavItemVisible(item: NavItem): boolean {
    if (item.permission === 'users:read') return canReadUsers
    if (item.permission === 'roles:read') return canReadRoles
    if (item.permission === 'mobile_apps:read') return canReadMobileApps
    if (item.permission === 'licenses:read') return canReadLicenses
    return true
  }

  const navGroups = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter(isNavItemVisible).map((item) => ({
      ...item,
      onClick: () => {
        navigationGuard.attempt(() => {
          navigate(item.key)
          setDrawerOpen(false)
        })
      },
    })),
  }))

  const footerItems = [
    {
      key: '/change-password',
      label: 'Alterar senha',
      icon: <KeyRound />,
      onClick: () => {
        navigate('/change-password')
        setDrawerOpen(false)
      },
    },
    {
      key: 'logout-all',
      label: 'Encerrar todas as sessões',
      icon: <ShieldOff />,
      onClick: () => logoutAll(),
    },
    {
      key: 'logout',
      label: 'Sair',
      icon: <LogOut />,
      onClick: () => logout(),
    },
  ]

  const logo = (
    <span className="text-sm font-semibold text-primary-700 tracking-tight">
      {screen === 'mobile' || collapsed ? 'N' : 'Navega Backoffice'}
    </span>
  )

  const sharedSidebarProps = {
    groups: navGroups,
    activeKey: getActiveKey(pathname),
    logo,
    footerItems,
  }

  const isMobile = screen === 'mobile'
  const isTablet = screen === 'tablet'

  const drawerEl = (
    <SidebarDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} {...sharedSidebarProps} />
  )

  const persistentSidebar = isMobile ? undefined : (
    <Sidebar
      {...sharedSidebarProps}
      collapsed={isTablet ? true : collapsed}
      onCollapsedChange={isTablet ? undefined : setCollapsed}
    />
  )

  function handleMenuToggle() {
    if (isMobile) {
      setDrawerOpen((o) => !o)
    } else if (!isTablet) {
      setCollapsed((c) => !c)
    }
  }

  const sidebarCollapsed = isTablet ? true : collapsed

  useEffect(() => {
    const width = isMobile ? '0px' : sidebarCollapsed ? '4rem' : '15rem'
    document.documentElement.style.setProperty('--sidebar-width', width)
    return () => {
      document.documentElement.style.removeProperty('--sidebar-width')
    }
  }, [isMobile, sidebarCollapsed])

  return (
    <>
      {isMobile && drawerEl}
      <AppShell
        sidebarCollapsed={sidebarCollapsed}
        sidebar={persistentSidebar}
        topbar={
          <Topbar
            breadcrumb={getBreadcrumbItems(pathname)}
            onMenuToggle={handleMenuToggle}
            searchSlot={null}
            rightSlot={<Avatar name={user?.name ?? user?.email ?? 'Usuário'} size="sm" />}
          />
        }
      >
        <Outlet />
      </AppShell>
    </>
  )
}
